import type { UseCase } from '../type/useCases';

export const USE_CASE_REGISTRY: UseCase[] = [

  {
    id: 'UC001',
    name: 'Loan Prospects',
    framework: 'CBUAE',
    geography: 'UAE',
    category: 'Lending',
    description: 'Identify the most promising loan prospects using customer profile, transaction history and behavioral signals.  ',
    tags: ['loan-prospects', 'lead-scoring', 'customer-analytics', 'lending'],
    technicalComplexity: 'MEDIUM',
  },
  {
    id: 'UC002',
    name: 'Loan Eligibility',
    framework: 'CBUAE',
    geography: 'UAE',
    category: 'Lending',
    description: 'Determine customer loan eligibility automatically using income, liabilities and credit information.',
    tags: ['loan-eligibility', 'underwriting', 'credit-risk', 'lending'],
    technicalComplexity: 'HIGH',
  },
  {
    id: 'UC003',
    name: 'Right Loan to Right Customer at Right Time',
    framework: 'CBUAE',
    geography: 'UAE',
    category: 'Lending',
    description: 'Recommend the right loan product to the right customer at the right time.',
    tags: ['recommendation', 'cross-sell', 'loan-product', 'personalization'],
    technicalComplexity: 'VERY_HIGH',
  },
  {
    id: 'UC004',
    name: 'Loan Affordability',
    framework: 'CBUAE',
    geography: 'UAE',
    category: 'Lending',
    description: 'Assess customer affordability and repayment capacity before approving loans.',
    tags: ['affordability', 'income-analysis', 'DBR', 'risk'],
    technicalComplexity: 'MEDIUM',
  },
  {
    id: 'UC005',
    name: 'Personalized Loan Offers',
    framework: 'CBUAE',
    geography: 'UAE',
    category: 'Lending',
    description: 'Generate personalized loan offers based on customer profile and financial behavior.',
    tags: ['personalized-offers', 'recommendation', 'customer-segmentation'],
    technicalComplexity: 'HIGH',
  },
  {
    id: 'UC006',
    name: 'KYC Document Verification',
    framework: 'CBUAE',
    geography: 'UAE',
    category: 'KYC',
    description: 'Verify KYC documents automatically using OCR and AI validation.',
    tags: ['kyc', 'document-verification', 'ocr', 'identity'],
    technicalComplexity: 'HIGH',
  },
  {
    id: 'UC007',
    name: 'Real-Time Identity Verification',
    framework: 'CBUAE',
    geography: 'UAE',
    category: 'KYC',
    description: 'Perform real-time customer identity verification during onboarding.',
    tags: ['identity-verification', 'real-time', 'face-match', 'onboarding'],
    technicalComplexity: 'MEDIUM',
  },
  {
    id: 'UC008',
    name: 'High-Risk Customer Identification',
    framework: 'CBUAE',
    geography: 'UAE',
    category: 'Risk',
    description: 'Identify high-risk customers before account opening or loan approval.',
    tags: ['risk', 'high-risk-customers', 'fraud', 'behavior-analysis'],
    technicalComplexity: 'MEDIUM',
  },
  {
    id: 'UC009',
    name: 'Digital Account Opening Assistant',
    framework: 'CBUAE',
    geography: 'UAE',
    category: 'Onboarding',
    description: 'Enable digital account opening with minimal manual intervention.',
    tags: ['account-opening', 'digital-onboarding', 'automation'],
    technicalComplexity: 'HIGH',
  },
  {
    id: 'UC010',
    name: 'Customer Onboarding Journey Optimization',
    framework: 'CBUAE',
    geography: 'UAE',
    category: 'Onboarding',
    description: 'Optimize the customer onboarding journey to improve experience and reduce drop-offs.',
    tags: ['onboarding', 'customer-experience', 'journey-optimization'],
    technicalComplexity: 'VERY_HIGH',
  },

  // ─────────────────────────────────────────
  // RBI (Reserve Bank of India) — 10 Use Cases
  // ─────────────────────────────────────────
  {
    id: 'UC011',
    name: 'Credit Bureau Analysis',
    framework: 'RBI',
    geography: 'India',
    category: 'Lending',
    description: 'Analyze bureau reports and credit history to improve lending decisions.',
    tags: ['credit-bureau', 'credit-score', 'risk-analysis'],
    technicalComplexity: 'MEDIUM',
  },
  {
    id: 'UC012',
    name: ' Bank Statement Analysis',
    framework: 'RBI',
    geography: 'India',
    category: 'Lending',
    description: 'Analyze customer bank statements to understand cash flows and repayment ability.',
    tags: ['bank-statements', 'cashflow-analysis', 'income'],
    technicalComplexity: 'HIGH',
  },
  {
    id: 'UC013',
    name: 'Income Verification Automation',
    framework: 'RBI',
    geography: 'India',
    category: 'Lending',
    description: 'Automate income verification using multiple financial data sources.',
    tags: ['income-verification', 'salary-analysis', 'automation'],
    technicalComplexity: 'VERY_HIGH',
  },
  {
    id: 'UC014',
    name: 'Financial Statement Analysis',
    framework: 'RBI',
    geography: 'India',
    category: 'Lending',
    description: 'Analyze financial statements to assess business performance and creditworthiness.',
    tags: ['financial-statements', 'balance-sheet', 'credit-analysis'],
    technicalComplexity: 'VERY_HIGH',
  },
  {
    id: 'UC015',
    name: 'Cash-Flow-Based Lending Assessment',
    framework: 'RBI',
    geography: 'India',
    category: 'Lending',
    description: 'Assess lending eligibility using actual cash-flow patterns.',
    tags: ['cashflow-lending', 'repayment-capacity', 'risk'],
    technicalComplexity: 'MEDIUM',
  },
  {
    id: 'UC016',
    name: 'SME Credit Risk Assessment',
    framework: 'RBI',
    geography: 'India',
    category: 'Risk',
    description: 'Assess credit risks associated with SME borrowers.',
    tags: ['sme', 'credit-risk', 'risk-assessment'],
    technicalComplexity: 'HIGH',
  },
  {
    id: 'UC017',
    name: 'Collateral Risk Assessment',
    framework: 'RBI',
    geography: 'India',
    category: 'Risk',
    description: 'Evaluate collateral risks and collateral values accurately.',
    tags: ['collateral', 'valuation', 'risk'],
    technicalComplexity: 'MEDIUM',
  },
  {
    id: 'UC018',
    name: 'Underwriting Recommendation Engine',
    framework: 'RBI',
    geography: 'India',
    category: 'Underwriting',
    description: 'Assist underwriters with AI-powered recommendations and decisions.',
    tags: ['underwriting', 'recommendation-engine', 'credit'],
    technicalComplexity: 'HIGH',
  },
  {
    id: 'UC019',
    name: 'Credit Memo Generation',
    framework: 'RBI',
    geography: 'India',
    category: 'Credit',
    description: 'Generate credit memos automatically from customer and loan data.',
    tags: ['credit-memo', 'automation', 'documentation'],
    technicalComplexity: 'HIGH',
  },
  {
    id: 'UC020',
    name: 'Liquidity Coverage Ratio Monitoring',
    framework: 'RBI',
    geography: 'India',
    category: 'Documents',
    description: 'Identify missing loan documents before processing applications.',
    tags: ['documents', 'missing-documents', 'validation'],
    technicalComplexity: 'VERY_HIGH',
  },

  // ─────────────────────────────────────────
  // SAMA (Saudi Central Bank) — 10 Use Cases
  // ─────────────────────────────────────────
  {
    id: 'UC021',
    name: 'SIMAH Credit Bureau Integration',
    framework: 'SAMA',
    geography: 'Saudi Arabia',
    category: 'Operations',
    description: 'Predict loan approval delays and identify bottlenecks in processing.',
    tags: ['approval-delay', 'prediction', 'turnaround-time'],
    technicalComplexity: 'MEDIUM',
  },
  {
    id: 'UC022',
    name: 'Missing Loan Document Detection',
    framework: 'SAMA',
    geography: 'Saudi Arabia',
    category: 'Productivity',
    description: 'Improve productivity of lending teams using AI assistants.',
    tags: ['productivity', 'assistant', 'operations'],
    technicalComplexity: 'HIGH',
  },
  {
    id: 'UC023',
    name: 'NAFIS Employment Verification',
    framework: 'SAMA',
    geography: 'Saudi Arabia',
    category: 'Collections',
    description: 'Recommend the next best action for at-risk borrowers.',
    tags: ['collections', 'next-best-action', 'borrowers'],
    technicalComplexity: 'MEDIUM',
  },
  {
    id: 'UC024',
    name: 'Loan Approval Delay Predictionn',
    framework: 'SAMA',
    geography: 'Saudi Arabia',
    category: 'Operations',
    description: 'Predict turnaround delays and prioritize pending applications.',
    tags: ['delay-prediction', 'operations', 'workflow'],
    technicalComplexity: 'MEDIUM',
  },
  {
    id: 'UC025',
    name: 'Lending Productivity Assistant',
    framework: 'SAMA',
    geography: 'Saudi Arabia',
    category: 'Productivity',
    description: 'Increase productivity of lending teams using AI copilots.',
    tags: ['copilot', 'productivity', 'lending'],
    technicalComplexity: 'HIGH',
  },
  {
    id: 'UC026',
    name: 'PDPL Consent Management',
    framework: 'SAMA',
    geography: 'Saudi Arabia',
    category: 'Collections',
    description: 'Predict borrower response behavior and improve collections.',
    tags: ['response-prediction', 'collections', 'customer-behavior'],
    technicalComplexity: 'MEDIUM',
  },
  {
    id: 'UC027',
    name: 'Delinquency Prediction Engine',
    framework: 'SAMA',
    geography: 'Saudi Arabia',
    category: 'Collections',
    description: 'Predict borrowers likely to miss their next payment.',
    tags: ['delinquency', 'payment-default', 'risk'],
    technicalComplexity: 'VERY_HIGH',
  },
  {
    id: 'UC028',
    name: 'Portfolio Risk Heatmap',
    framework: 'SAMA',
    geography: 'Saudi Arabia',
    category: 'Risk',
    description: 'Visualize portfolio risks before they affect performance.',
    tags: ['portfolio-risk', 'heatmap', 'risk-visualization'],
    technicalComplexity: 'HIGH',
  },
  {
    id: 'UC029',
    name: 'Exposure Concentration Analysis',
    framework: 'SAMA',
    geography: 'Saudi Arabia',
    category: 'Risk',
    description: 'Detect concentration risks across industries, geographies and segments.',
    tags: ['concentration-risk', 'portfolio', 'exposure'],
    technicalComplexity: 'VERY_HIGH',
  },
  {
    id: 'UC030',
    name: 'Real Estate Financing Assessment',
    framework: 'SAMA',
    geography: 'Saudi Arabia',
    category: 'Collections',
    description: 'Recommend the best actions for at-risk borrowers to improve recovery outcomes.',
    tags: ['next-best-action', 'collections', 'recovery'],
    technicalComplexity: 'VERY_HIGH',
  },
];

export const USE_CASES_BY_FRAMEWORK = {
  CBUAE: USE_CASE_REGISTRY.filter(uc => uc.framework === 'CBUAE'),
  RBI: USE_CASE_REGISTRY.filter(uc => uc.framework === 'RBI'),
  SAMA: USE_CASE_REGISTRY.filter(uc => uc.framework === 'SAMA'),
};
